import {
  type Document,
  type Block,
  type Inline,
  type Text as ContentfulText,
  type TopLevelBlock,
  BLOCKS,
  MARKS,
  INLINES,
} from '@contentful/rich-text-types';
import React, { type ReactNode } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { isInternalUrl } from 'utils';
import { DOCUMENT_TARGET } from '../../utils/i18nConstants';
import Text, { TextTag, TEXT_VARIANT } from '@pixum/text';
import { RichTextCookieBannerLink } from './RichTextCookieBannerLink';

// catches any tag, opening or closing
const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;

export interface RichTextDocument extends Document {
  data: object;
}

/**
 * Renders a paragraph node.
 *
 * @param {Block | Inline} node - The block or inline node to render.
 * @param {ReactNode | null} children - The child nodes to render within the paragraph.
 * @returns {ReactNode | null} A ReactNode representing the paragraph, or null if empty.
 */
function renderParagraph(node: Block | Inline, children: ReactNode | null): ReactNode | null {
  const hasMultipleNodes = node.content.length > 1;
  const hasUniqueNode = node.content.length === 1;
  const hasText = hasUniqueNode && node.content[0].nodeType === 'text' && !!node.content[0].value?.trim();

  if (!(hasMultipleNodes || hasText)) return null;

  const containsHtml = node.content.some(
    content => content.nodeType === 'text' && HTML_TAG_REGEX.test((content as ContentfulText).value),
  );

  if (containsHtml) {
    const html = node.content
      .map(content => (content.nodeType === 'text' ? String((content as ContentfulText).value ?? '') : ''))
      .join('');

    return <p dangerouslySetInnerHTML={{ __html: html }} />;
  }

  // normal rich-text path (no raw HTML in text nodes)
  return <p>{children}</p>;
}

/**
 * Renders bold text.
 *
 * @returns {ReactNode} The bold text as a ReactNode.
 */
function renderBold(text: ReactNode): ReactNode {
  return <b>{text}</b>;
}

type RichTextNode = { data?: { uri?: string }; content?: Array<{ value?: string }> };

const getAnchor = (node: RichTextNode, linkOnNewTab = false) => {
  const url = node.data?.uri;
  const linkText = node.content?.[0]?.value || url || '';

  if (url?.includes('showSecondLayer')) {
    return <RichTextCookieBannerLink text={linkText} />;
  }

  const isInternal = isInternalUrl(url ?? '');

  const params: Record<string, string> = {
    target: DOCUMENT_TARGET.Self,
  };

  if (!isInternal || linkOnNewTab) {
    params.target = DOCUMENT_TARGET.Blank;
    params.rel = 'noreferrer';
  }

  return (
    <a href={url} {...params}>
      {linkText}
    </a>
  );
};

function renderHyperLink(node: Block | Inline): ReactNode {
  return getAnchor(node as unknown as RichTextNode);
}

function renderHeading3(_node: Block | Inline, children: ReactNode | null): ReactNode {
  return (
    <Text tag={TextTag.H3} variant={TEXT_VARIANT.TITLE_3}>
      {children}
    </Text>
  );
}

export const nodeOptions = {
  [BLOCKS.PARAGRAPH]: renderParagraph,
  [BLOCKS.HEADING_3]: renderHeading3,
  [INLINES.HYPERLINK]: renderHyperLink,
};

/**
 * Mark rendering options for Contentful Rich Text marks.
 */
const markOptions = {
  [MARKS.BOLD]: renderBold,
};

/**
 * Renders a string as ReactNodes, splitting on newlines and inserting line breaks.
 *
 * @param {string} text - The text to render.
 * @returns {ReactNode[]} The rendered ReactNodes.
 */
function renderTextOption(text: string): ReactNode[] {
  return text.split('\n').reduce<ReactNode[]>((acc, part, i) => {
    if (i > 0) acc.push(<br key={`${i}-br`} />);
    acc.push(part);

    return acc;
  }, []);
}

/**
 * This function converts a Contentful Rich Text document into a plain string by extracting and concatenating the text
 * content. This is helpful when content creators put html code into the rich text editor. In this case all content is
 * text and can be safely extracted by the value property. Be careful when using this function as it can only be used
 * when content creators use html code in the rich text editor of contentful.
 *
 * @param richText - The Contentful Rich Text document to convert.
 * @returns A plain string representation of the rich text content.
 */
function richTextJsonToString(richText: RichTextDocument | undefined): string {
  return richText
    ? richText.content
        .map((node: TopLevelBlock) => {
          if ((node.nodeType as string) === 'text') {
            return (node as unknown as { value: string }).value;
          }

          if ('content' in node) {
            return richTextJsonToString({ ...node, nodeType: 'document', data: {} } as RichTextDocument);
          }

          return '';
        })
        .join(' ')
    : '';
}

/**
 * Renders Contentful Rich Text content as React components.
 *
 * @param {Document} node - The Contentful Rich Text document.
 * @returns {ReactNode | null} The rendered React element or null if no node is provided.
 */
function RichTextContent({ node }: { node?: RichTextDocument }): ReactNode | null {
  if (!node) {
    return null;
  }

  const customNodeOption = {
    ...nodeOptions,
  };

  const options = {
    renderMark: markOptions,
    renderNode: customNodeOption,
    renderText: renderTextOption,
  };

  return documentToReactComponents(node, options as any); // eslint-disable-line @typescript-eslint/no-explicit-any
}

export { RichTextContent, renderTextOption, renderParagraph, renderBold, renderHyperLink, richTextJsonToString };
